import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Calendar,
  MapPin,
  Building2,
  Users,
  DollarSign,
  Play,
  Pause,
  MoreHorizontal,
  Briefcase,
  ArrowLeft,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { recruitmentAPI, departmentAPI } from "@/lib/api";
import JobOfferForm from "./JobOfferForm";
import JobOfferDetails from "./JobOfferDetails";

const JobOffersList = ({
  onBack,
  autoOpenCreate = false,
  onModalStateChange,
}) => {
  const [jobs, setJobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [contractTypeFilter, setContractTypeFilter] = useState("all");
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(autoOpenCreate);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states for modal actions
  const [createFormState, setCreateFormState] = useState({
    loading: false,
    handleSubmit: null,
  });
  const [editFormState, setEditFormState] = useState({
    loading: false,
    handleSubmit: null,
  });
  const [selectedJob, setSelectedJob] = useState(null);

  const { toast } = useToast();
  const { user } = useAuth();

  // Handle autoOpenCreate prop changes
  useEffect(() => {
    if (autoOpenCreate) {
      setShowCreateModal(true);
    }
  }, [autoOpenCreate]);

  // Notify parent when modal state changes
  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(showCreateModal);
    }
  }, [showCreateModal, onModalStateChange]);

  useEffect(() => {
    fetchJobs();
    fetchDepartments();
  }, [
    searchTerm,
    statusFilter,
    departmentFilter,
    contractTypeFilter,
    pagination.current_page,
  ]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        limit: pagination.items_per_page,
        search: searchTerm || undefined,
        statut: statusFilter !== "all" ? statusFilter : undefined,
        departement_id:
          departmentFilter !== "all" ? departmentFilter : undefined,
        type_contrat:
          contractTypeFilter !== "all" ? contractTypeFilter : undefined,
      };

      const response = await recruitmentAPI.getJobs(params);
      setJobs(response.data.data.jobs);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les offres d'emploi",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentAPI.getAll();
      setDepartments(response.data.data.departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setDepartments([]); // Set to empty array on error
    }
  };

  const handleCreate = async (jobData) => {
    try {
      await recruitmentAPI.createJob(jobData);
      toast({
        title: "Succès",
        description: "Offre d'emploi créée avec succès",
      });
      setShowCreateModal(false);
      fetchJobs();
    } catch (error) {
      console.error("Error creating job:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la création de l'offre",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (jobData) => {
    try {
      await recruitmentAPI.updateJob(selectedJob.id, jobData);
      toast({
        title: "Succès",
        description: "Offre d'emploi mise à jour avec succès",
      });
      setShowEditModal(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (error) {
      console.error("Error updating job:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour de l'offre",
        variant: "destructive",
      });
    }
  };

  const handlePublish = async (job) => {
    try {
      await recruitmentAPI.publishJob(job.id);
      toast({
        title: "Succès",
        description: "Offre d'emploi publiée avec succès",
      });
      fetchJobs();
    } catch (error) {
      console.error("Error publishing job:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la publication de l'offre",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await recruitmentAPI.deleteJob(selectedJob.id);
      toast({
        title: "Succès",
        description: "Offre d'emploi supprimée avec succès",
      });
      setShowDeleteModal(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (error) {
      console.error("Error deleting job:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la suppression de l'offre",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      brouillon: "bg-gray-100 text-gray-800",
      ouverte: "bg-green-100 text-green-800",
      fermee: "bg-red-100 text-red-800",
      suspendue: "bg-yellow-100 text-yellow-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      brouillon: "Brouillon",
      ouverte: "Ouverte",
      fermee: "Fermée",
      suspendue: "Suspendue",
    };
    return labels[status] || status;
  };

  const getContractTypeLabel = (type) => {
    const labels = {
      cdi: "CDI",
      cdd: "CDD",
      stage: "Stage",
      interim: "Intérim",
      freelance: "Freelance",
      apprentissage: "Apprentissage",
    };
    return labels[type] || type;
  };

  const canModify = user?.role === "admin" || user?.role === "rh";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au tableau de bord
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Offres d'emploi
            </h1>
            <p className="text-gray-600">
              Gérez vos offres d'emploi et publications
            </p>
          </div>
        </div>

        {canModify && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle offre
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="ouverte">Ouverte</SelectItem>
                <SelectItem value="fermee">Fermée</SelectItem>
                <SelectItem value="suspendue">Suspendue</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={departmentFilter}
              onValueChange={setDepartmentFilter}
            >
              <SelectTrigger>
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les départements</SelectItem>
                {Array.isArray(departments) &&
                  departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.nom}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select
              value={contractTypeFilter}
              onValueChange={setContractTypeFilter}
            >
              <SelectTrigger>
                <SelectValue placeholder="Type de contrat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="cdi">CDI</SelectItem>
                <SelectItem value="cdd">CDD</SelectItem>
                <SelectItem value="stage">Stage</SelectItem>
                <SelectItem value="interim">Intérim</SelectItem>
                <SelectItem value="freelance">Freelance</SelectItem>
                <SelectItem value="apprentissage">Apprentissage</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setDepartmentFilter("all");
                setContractTypeFilter("all");
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              Réinitialiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Job Offers List */}
      <div className="grid gap-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : jobs.length > 0 ? (
          jobs.map((job) => (
            <Card key={job.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="h-6 w-6 text-blue-600" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {job.titre}
                          </h3>
                          <Badge className={getStatusColor(job.statut)}>
                            {getStatusLabel(job.statut)}
                          </Badge>
                          {job.type_contrat && (
                            <Badge variant="outline">
                              {getContractTypeLabel(job.type_contrat)}
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                          {job.departement && (
                            <div className="flex items-center gap-1">
                              <Building2 className="h-4 w-4" />
                              <span>{job.departement.nom}</span>
                            </div>
                          )}

                          {job.lieu_travail && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              <span>{job.lieu_travail}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>
                              Créée le{" "}
                              {new Date(job.date_creation).toLocaleDateString(
                                "fr-FR"
                              )}
                            </span>
                          </div>

                          {job.stats.total_candidatures > 0 && (
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              <span>
                                {job.stats.total_candidatures} candidature(s)
                              </span>
                            </div>
                          )}
                        </div>

                        {(job.salaire_min || job.salaire_max) && (
                          <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                            <DollarSign className="h-4 w-4" />
                            <span>
                              {job.salaire_min && job.salaire_max
                                ? `${job.salaire_min.toLocaleString()} - ${job.salaire_max.toLocaleString()} MAD`
                                : job.salaire_min
                                ? `À partir de ${job.salaire_min.toLocaleString()} MAD`
                                : `Jusqu'à ${job.salaire_max.toLocaleString()} MAD`}
                            </span>
                          </div>
                        )}

                        {job.description && (
                          <p className="text-gray-700 text-sm line-clamp-2">
                            {job.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedJob(job);
                        setShowDetailsModal(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    {canModify && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedJob(job);
                              setShowEditModal(true);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>

                          {job.statut === "brouillon" && (
                            <DropdownMenuItem
                              onClick={() => handlePublish(job)}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Publier
                            </DropdownMenuItem>
                          )}

                          {job.statut === "ouverte" && (
                            <DropdownMenuItem
                              onClick={() =>
                                recruitmentAPI
                                  .updateJob(job.id, { statut: "suspendue" })
                                  .then(fetchJobs)
                              }
                            >
                              <Pause className="h-4 w-4 mr-2" />
                              Suspendre
                            </DropdownMenuItem>
                          )}

                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              setSelectedJob(job);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucune offre d'emploi trouvée</p>
                {canModify && (
                  <Button
                    className="mt-4"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer la première offre
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Page {pagination.current_page} sur {pagination.total_pages}(
            {pagination.total_items} résultat(s))
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  current_page: prev.current_page - 1,
                }))
              }
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  current_page: prev.current_page + 1,
                }))
              }
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col top-[5%] translate-y-0 p-0">
            {/* Fixed Header */}
            <div className="p-6 border-b">
              <DialogHeader>
                <DialogTitle>Créer une offre d'emploi</DialogTitle>
                <DialogDescription>
                  Créez une nouvelle offre d'emploi pour votre organisation
                </DialogDescription>
              </DialogHeader>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <JobOfferForm
                onSubmit={handleCreate}
                onCancel={() => setShowCreateModal(false)}
                departments={departments}
                showActions={false}
                onFormSubmit={setCreateFormState}
              />
            </div>

            {/* Fixed Footer */}
            <div className="p-6 border-t bg-background">
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  disabled={createFormState.loading}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (createFormState.handleSubmit) {
                      const event = { preventDefault: () => {} };
                      createFormState.handleSubmit(event);
                    }
                  }}
                  disabled={createFormState.loading}
                >
                  {createFormState.loading
                    ? "Enregistrement..."
                    : "Créer l'offre"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showEditModal && selectedJob && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col top-[5%] translate-y-0 p-0">
            {/* Fixed Header */}
            <div className="p-6 border-b">
              <DialogHeader>
                <DialogTitle>Modifier l'offre d'emploi</DialogTitle>
                <DialogDescription>
                  Modifiez les détails de l'offre d'emploi
                </DialogDescription>
              </DialogHeader>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <JobOfferForm
                jobOffer={selectedJob}
                onSubmit={handleEdit}
                onCancel={() => {
                  setShowEditModal(false);
                  setSelectedJob(null);
                }}
                departments={departments}
                showActions={false}
                onFormSubmit={setEditFormState}
              />
            </div>

            {/* Fixed Footer */}
            <div className="p-6 border-t bg-background">
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedJob(null);
                  }}
                  disabled={editFormState.loading}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (editFormState.handleSubmit) {
                      const event = { preventDefault: () => {} };
                      editFormState.handleSubmit(event);
                    }
                  }}
                  disabled={editFormState.loading}
                >
                  {editFormState.loading
                    ? "Enregistrement..."
                    : "Mettre à jour"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showDetailsModal && selectedJob && (
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto top-[5%] translate-y-0">
            <JobOfferDetails
              jobId={selectedJob.id}
              onClose={() => {
                setShowDetailsModal(false);
                setSelectedJob(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {showDeleteModal && selectedJob && (
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Supprimer l'offre d'emploi</DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer l'offre "{selectedJob.titre}"
                ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedJob(null);
                }}
              >
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default JobOffersList;
